import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Cookie, CheckCircle, XCircle } from 'lucide-react';
import PageTitle from '../hooks/PageTitle';

const CookiePolicy = () => {
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  });

  const handleToggle = (category) => {
    if (category !== 'necessary') {
      setCookiePreferences(prev => ({
        ...prev,
        [category]: !prev[category]
      }));
    }
  };

  const handleAcceptAll = () => {
    setCookiePreferences({
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    });
  };

  const handleRejectAll = () => {
    setCookiePreferences({
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    });
  };

  const sections = [
    { id: '1', title: '1. Introduction', href: '#introduction' },
    { id: '2', title: '2. What Are Cookies?', href: '#what-are-cookies' },
    { id: '3', title: '3. Types of Cookies', href: '#types' },
    { id: '4', title: '4. Cookies We Use', href: '#cookies-we-use' },
    { id: '5', title: '5. Managing Cookies', href: '#managing' },
    { id: '6', title: '6. Updates', href: '#updates' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PageTitle title="Cookie Policy" />
      {/* Header */}
      <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center text-sm text-gray-300 mb-4">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span>Cookie Policy</span>
          </div>
          <div className="flex items-center space-x-4 mb-6">
            <div className="bg-white/10 p-3 rounded-lg">
              <Cookie className="h-8 w-8" />
            </div>
            <h1 className="text-4xl md:text-5xl font-serif">Cookie Policy</h1>
          </div>
          <p className="text-lg text-gray-300 max-w-3xl">
            Learn about how Ashbourne Law uses cookies and similar technologies on our website.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Cookie Preferences Banner */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-serif text-gray-900 mb-4">Manage Your Cookie Preferences</h2>
          <p className="text-gray-600 mb-6">
            You can control which cookies we use on our website. Necessary cookies are always enabled as they are essential for the website to function.
          </p>

          <div className="space-y-4 mb-6">
            {/* Necessary Cookies */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Necessary Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">Essential for website functionality. Cannot be disabled.</p>
              </div>
              <div className="ml-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Functional Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">Enable enhanced functionality and personalization.</p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => handleToggle('functional')}
                  className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                  style={{ backgroundColor: cookiePreferences.functional ? '#10b981' : '#d1d5db' }}
                >
                  <span
                    className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    style={{ transform: cookiePreferences.functional ? 'translateX(1.25rem)' : 'translateX(0)' }}
                  />
                </button>
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Analytics Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">Help us understand how visitors use our website.</p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => handleToggle('analytics')}
                  className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                  style={{ backgroundColor: cookiePreferences.analytics ? '#10b981' : '#d1d5db' }}
                >
                  <span
                    className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    style={{ transform: cookiePreferences.analytics ? 'translateX(1.25rem)' : 'translateX(0)' }}
                  />
                </button>
              </div>
            </div>

            {/* Marketing Cookies */}
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-gray-200">
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Marketing Cookies</h3>
                <p className="text-sm text-gray-600 mt-1">Used to track visitors across websites for advertising purposes.</p>
              </div>
              <div className="ml-4">
                <button
                  onClick={() => handleToggle('marketing')}
                  className="relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none"
                  style={{ backgroundColor: cookiePreferences.marketing ? '#10b981' : '#d1d5db' }}
                >
                  <span
                    className="pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                    style={{ transform: cookiePreferences.marketing ? 'translateX(1.25rem)' : 'translateX(0)' }}
                  />
                </button>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Accept All
            </button>
            <button
              onClick={handleRejectAll}
              className="px-6 py-2 bg-gray-200 text-gray-900 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Reject All
            </button>
            <button
              onClick={() => {
                console.log('Saved preferences:', cookiePreferences);
                alert('Your cookie preferences have been saved.');
              }}
              className="px-6 py-2 bg-white border border-gray-300 text-gray-900 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Save Preferences
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-gray-50 rounded-lg p-6">
              <h3 className="font-medium text-gray-900 mb-4">Sections</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <a
                    key={section.id}
                    href={section.href}
                    className="block text-sm text-gray-600 hover:text-gray-900 transition-colors py-1"
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
            </div>
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="prose prose-lg max-w-none">
              {/* Section 1 */}
              <section id="introduction" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">1. Introduction</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    This Cookie Policy explains how Ashbourne Law Limited ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>") uses cookies and similar tracking technologies on our website <a href="https://www.ashbournelaw.com" className="text-gray-900 underline">www.ashbournelaw.com</a> (the "<strong>Website</strong>").
                  </p>
                  <p>
                    By using our Website, you consent to the use of cookies in accordance with this Cookie Policy. This policy should be read together with our <Link to="/privacy-policy" className="text-gray-900 underline">Privacy Policy</Link>.
                  </p>
                </div>
              </section>

              {/* Section 2 */}
              <section id="what-are-cookies" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">2. What Are Cookies?</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    Cookies are small text files that are stored on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners.
                  </p>
                  <p>
                    Cookies can be "persistent" (remaining on your device until deleted or expired) or "session" (deleted when you close your browser).
                  </p>
                  <p>
                    Cookies can be "first-party" (set by the website you're visiting) or "third-party" (set by a different website, such as an analytics or advertising service).
                  </p>
                </div>
              </section>

              {/* Section 3 */}
              <section id="types" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">3. Types of Cookies We Use</h2>
                <div className="space-y-6 text-gray-700">
                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-3">3.1 Necessary Cookies</h3>
                    <p>
                      These cookies are essential for the Website to function properly. They enable basic functions like page navigation, access to secure areas, and remembering your cookie preferences. The Website cannot function properly without these cookies.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Legal basis:</strong> These cookies are strictly necessary for the provision of services you have requested.
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-3">3.2 Functional Cookies</h3>
                    <p>
                      These cookies enable enhanced functionality and personalization, such as remembering your preferences (e.g., language, region), login details, and customized content. They may be set by us or by third-party providers.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Legal basis:</strong> Your consent (which you can withdraw at any time).
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-3">3.3 Analytics Cookies</h3>
                    <p>
                      These cookies help us understand how visitors interact with our Website by collecting and reporting information anonymously. This helps us improve the Website's performance and user experience. We may use services like Google Analytics.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Legal basis:</strong> Your consent (which you can withdraw at any time).
                    </p>
                  </div>

                  <div>
                    <h3 className="text-xl font-medium text-gray-900 mb-3">3.4 Marketing/Advertising Cookies</h3>
                    <p>
                      These cookies are used to track visitors across websites. They are used to display relevant advertisements to users and to measure the effectiveness of advertising campaigns. They may be set by third-party advertising networks.
                    </p>
                    <p className="text-sm text-gray-600 mt-2">
                      <strong>Legal basis:</strong> Your consent (which you can withdraw at any time).
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 4 */}
              <section id="cookies-we-use" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">4. Cookies We Use</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    Below is a list of the cookies we currently use on our Website:
                  </p>

                  <div className="overflow-x-auto">
                    <table className="min-w-full border border-gray-200 text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="border border-gray-200 px-4 py-2 text-left font-medium text-gray-900">Cookie Name</th>
                          <th className="border border-gray-200 px-4 py-2 text-left font-medium text-gray-900">Category</th>
                          <th className="border border-gray-200 px-4 py-2 text-left font-medium text-gray-900">Purpose</th>
                          <th className="border border-gray-200 px-4 py-2 text-left font-medium text-gray-900">Duration</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="border border-gray-200 px-4 py-2">ashbourne_session</td>
                          <td className="border border-gray-200 px-4 py-2">Necessary</td>
                          <td className="border border-gray-200 px-4 py-2">Maintains your session while browsing the site</td>
                          <td className="border border-gray-200 px-4 py-2">Session</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-2">cookie_consent</td>
                          <td className="border border-gray-200 px-4 py-2">Necessary</td>
                          <td className="border border-gray-200 px-4 py-2">Stores your cookie preferences</td>
                          <td className="border border-gray-200 px-4 py-2">1 year</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-2">_ga</td>
                          <td className="border border-gray-200 px-4 py-2">Analytics</td>
                          <td className="border border-gray-200 px-4 py-2">Google Analytics - distinguishes users</td>
                          <td className="border border-gray-200 px-4 py-2">2 years</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-2">_gid</td>
                          <td className="border border-gray-200 px-4 py-2">Analytics</td>
                          <td className="border border-gray-200 px-4 py-2">Google Analytics - distinguishes users</td>
                          <td className="border border-gray-200 px-4 py-2">24 hours</td>
                        </tr>
                        <tr>
                          <td className="border border-gray-200 px-4 py-2">_gat</td>
                          <td className="border border-gray-200 px-4 py-2">Analytics</td>
                          <td className="border border-gray-200 px-4 py-2">Google Analytics - throttles request rate</td>
                          <td className="border border-gray-200 px-4 py-2">1 minute</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="text-sm text-gray-600 mt-4">
                    <strong>Note:</strong> This list may not be exhaustive and may be updated from time to time. Third-party services we use may also set their own cookies.
                  </p>
                </div>
              </section>

              {/* Section 5 */}
              <section id="managing" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">5. How to Manage Cookies</h2>
                <div className="space-y-4 text-gray-700">
                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">5.1 Cookie Preferences Tool</h3>
                  <p>
                    You can manage your cookie preferences using the tool at the top of this page. Your preferences will be saved and applied during your visit.
                  </p>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">5.2 Browser Settings</h3>
                  <p>
                    Most web browsers allow you to control cookies through their settings. You can set your browser to:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Accept all cookies</li>
                    <li>Reject all cookies</li>
                    <li>Notify you when a cookie is set</li>
                    <li>Delete cookies after you close your browser</li>
                  </ul>
                  <p>
                    Please note that blocking all cookies may impact your experience on our Website, and some features may not work properly.
                  </p>
                  <p>
                    For more information on managing cookies in your browser:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><a href="https://support.google.com/chrome/answer/95647" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
                    <li><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
                    <li><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Safari</a></li>
                    <li><a href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">5.3 Third-Party Opt-Out Tools</h3>
                  <p>
                    You can also opt out of third-party cookies using industry opt-out tools:
                  </p>
                  <ul className="list-disc pl-6 space-y-1">
                    <li><a href="https://www.youronlinechoices.com/" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Your Online Choices (EU)</a></li>
                    <li><a href="https://optout.networkadvertising.org/" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Network Advertising Initiative (US)</a></li>
                    <li><a href="https://optout.aboutads.info/" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Digital Advertising Alliance (US)</a></li>
                  </ul>

                  <h3 className="text-xl font-medium text-gray-900 mt-6 mb-3">5.4 Google Analytics Opt-Out</h3>
                  <p>
                    To opt out of Google Analytics tracking across all websites, you can install the <a href="https://tools.google.com/dlpage/gaoptout" className="text-gray-900 underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
                  </p>
                </div>
              </section>

              {/* Section 6 */}
              <section id="updates" className="mb-12">
                <h2 className="text-2xl font-serif text-gray-900 mb-4">6. Updates to This Policy</h2>
                <div className="space-y-4 text-gray-700">
                  <p>
                    We may update this Cookie Policy from time to time to reflect changes in our use of cookies or changes in applicable law. Any updates will be posted on this page with a revised "Last Updated" date.
                  </p>
                  <p>
                    We encourage you to review this Cookie Policy periodically.
                  </p>
                </div>
              </section>

              {/* Contact */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mt-8">
                <h3 className="font-medium text-gray-900 mb-2">Questions About Cookies?</h3>
                <p className="text-gray-600 text-sm mb-4">
                  If you have any questions about our use of cookies, please contact our Privacy Manager at <a href="mailto:privacy@ashbournelaw.com" className="text-gray-900 underline">privacy@ashbournelaw.com</a> or see our <Link to="/privacy-policy" className="text-gray-900 underline">Privacy Policy</Link>.
                </p>
              </div>

              <p className="text-sm text-gray-500 mt-8">
                Last updated: December 2024
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicy;